import { useMemo } from 'react';
import { MarkdownEditor } from './MarkdownEditor';
import { MarkdownViewer, MarkdownViewerStyleConfig } from './MarkdownViewer';

interface UseMarkdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  editorClassName?: string;
  viewerClassName?: string;
  viewerStyleConfig?: MarkdownViewerStyleConfig;
  editorMinHeight?: string;
  editorMaxHeight?: string;
}

interface UseMarkdownReturn {
  MarkdownEditorComponent: JSX.Element;
  MarkdownViewerComponent: JSX.Element;
}

/**
 * Custom hook for getting memoized Markdown Editor and Viewer components
 *
 * @param value - The markdown content state
 * @param onChange - Function to update the markdown content
 * @param placeholder - Optional placeholder text for the editor
 * @param editorClassName - Optional CSS class for the editor
 * @param viewerClassName - Optional CSS class for the viewer
 *
 * @returns Object containing memoized MarkdownEditorComponent and MarkdownViewerComponent
 *
 * @example
 * ```tsx
 * const [markdown, setMarkdown] = useState('');
 * const { MarkdownEditorComponent, MarkdownViewerComponent } = useMarkdown({
 *   value: markdown,
 *   onChange: setMarkdown,
 *   placeholder: 'Enter your notes...'
 * });
 *
 * return (
 *   <div>
 *     {MarkdownEditorComponent}
 *     {MarkdownViewerComponent}
 *   </div>
 * );
 * ```
 */
export const useMarkdown = ({
  value,
  onChange,
  placeholder,
  editorClassName,
  viewerClassName,
  viewerStyleConfig,
  editorMinHeight,
  editorMaxHeight,
}: UseMarkdownProps): UseMarkdownReturn => {
  // Memoize the Editor component to prevent unnecessary re-renders
  const MarkdownEditorComponent = useMemo(
    () => (
      <MarkdownEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={editorClassName}
        minHeight={editorMinHeight}
        maxHeight={editorMaxHeight}
      />
    ),
    [value, onChange, placeholder, editorClassName, editorMinHeight, editorMaxHeight],
  );

  // Memoize the Viewer component to prevent unnecessary re-renders
  const MarkdownViewerComponent = useMemo(
    () => (
      <MarkdownViewer content={value} className={viewerClassName} styleConfig={viewerStyleConfig} />
    ),
    [value, viewerClassName, viewerStyleConfig],
  );

  return {
    MarkdownEditorComponent,
    MarkdownViewerComponent,
  };
};
